/* eslint-disable eqeqeq */
import { handleTabToNext } from '../../formKit';
import { ACCESSORY_FORM_CONFIGS } from '../accessoryFormConfigs';
import { useAccessoryForm } from '../useAccessoryForm';
import { AccessoryHeading, AccSubtab, APSelect, ASelect } from '../accessoryFieldKit';
import type { AccessoryFormProps } from '../AccessoryFormHost';

/**
 * Phase 6e2b-1 — STARTER form (accessories/starter/{form.js 65,
 * form.tmpl 151}, read in full). STRUCTURAL PORT ONLY — dev3 has no live
 * starter rows (verified; smoke note).
 *  - contactor + overload catalogue fields (BOTH reset-exempt §49.4:616 /
 *    parity 312 — no remove_accessory_from_canvas, no size nulls);
 *  - NO position (auto-position exemption §49.6:306), NO mounting popup
 *    (setupMounting.withPopup:false — legacy's inline render path never
 *    fires because popupContentContainer is never set; the mounting subtab
 *    content is the tmpl's own Wiring row + hidden enclosure checkbox);
 *  - per-subtab heading chrome INSIDE the li (FormSubtab headerChrome).
 *
 * The four params selects carry TWO classes in form.tmpl (:19, :31, :78, :90) —
 * `<componentName>_params_field` AND `starter_<componentName>_params_field` —
 * and starter is the only accessory whose tmpl does that, because it is the
 * only one whose paramsSourceField is not `<componentName>_params`. The second
 * class is what changeSelectValue's DOM scan matches on (it resolves the class
 * to a paramsSourceField), so dropping it made the sole-option auto-select on
 * add a no-op here. APSelect only emits the first, hence the extraClass.
 */
const config = ACCESSORY_FORM_CONFIGS.starter;

const StarterForm = ({ row, section, tunnel, expanded }: AccessoryFormProps) => {
  const api = useAccessoryForm({
    config,
    row,
    section,
    tunnel,
    expanded,
    labeledSubtabs: ['contactor', 'overload', 'mounting'],
  });
  const { model } = api;
  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const cid = row.id;
  const contactor = (name: string) =>
    api.catalogueFields.master_starter_contactor_id?.paramValue(name);
  const overload = (name: string) =>
    api.catalogueFields.master_starter_overload_id?.paramValue(name);

  const echoRow = (unit: string | null, value: unknown, label: string) => (
    <li>
      <div>
        {unit && <span className="unit">{unit}</span>}
        <input
          type="text"
          data-formatter_type="decimal"
          autoComplete="off"
          className="non-clickable"
          value={String(value ?? '')}
          readOnly
          tabIndex={-1}
          onChange={() => undefined}
        />
        <span className="label">{label}</span>
      </div>
    </li>
  );

  return (
    <>
      <form ref={(el) => { api.formRef.current = el; }} onKeyDown={handleTabToNext}>
        <ul className="inputs-group sublist">
          <AccSubtab
            api={api}
            type="starter"
            cid={cid}
            name="contactor"
            label="Contactor"
            headerChrome={
              <AccessoryHeading api={api} target="master_starter_contactor_id" visClass="vis-contactor" />
            }
          >
            <APSelect api={api} target="master_starter_contactor_id" name="mfg_id" label="Manufacturer" required cmsPath="contactor.manufacturer" extraClass="starter_contactor_params_field" />
            <APSelect
              api={api}
              target="master_starter_contactor_id"
              name="model"
              label="Model"
              required
              cmsPath="contactor.model"
              labelKey="model"
              valueKey="model"
              extraClass="contactor_model starter_contactor_params_field"
            />
            {echoRow('HP', contactor('rated_power'), 'Rated Power')}
            {echoRow('VAC', contactor('rated_op_voltage'), 'Rated Operating Voltage')}
            {echoRow('Amp', contactor('rated_op_current'), 'Rated Operating Current')}
          </AccSubtab>

          <AccSubtab
            api={api}
            type="starter"
            cid={cid}
            name="overload"
            label="Overload"
            headerChrome={
              <AccessoryHeading api={api} target="master_starter_overload_id" visClass="vis-starter-overload" />
            }
          >
            <APSelect api={api} target="master_starter_overload_id" name="mfg_id" label="Manufacturer" required cmsPath="overload.manufacturer" extraClass="starter_overload_params_field" />
            <APSelect
              api={api}
              target="master_starter_overload_id"
              name="model"
              label="Model"
              required
              cmsPath="overload.model"
              labelKey="model"
              valueKey="model"
              extraClass="overload_model starter_overload_params_field"
            />
            {echoRow('VAC', overload('rated_op_voltage'), 'Rated Operating Voltage')}
            {echoRow('Amp', overload('amp_min'), 'Amperage')}
            {echoRow(null, overload('amp_max'), 'Class')}
          </AccSubtab>

          <AccSubtab api={api} type="starter" cid={cid} name="mounting" label="Mounting">
            <ASelect api={api} name="wiring_id" label="Wiring" cmsPath="wiring" hideNone={false} />
            <li className="hide">
              <div>
                <div className="checkboxes-group">
                  <label>
                    <input
                      type="checkbox"
                      className="housed_in_enclosure model_field"
                      checked={!!model.get('enclosure_id')}
                      name="enclosure_id"
                      tabIndex={-1}
                      onChange={(e) => api.commitModelCheckbox('enclosure_id', e.target.checked)}
                    />
                  </label>
                </div>
                <span className="label">Housed in Enclosure</span>
              </div>
            </li>
          </AccSubtab>
        </ul>
      </form>
    </>
  );
};

export default StarterForm;
